<template>
    <div id="contents" class="contents spirit-of-japan">
        <JsonLd :json="jsonld" />
        <section class="main">
            <div class="texts">
                <div class="inner">
                    <p class="sub">A Journey to the True Spirit of Japan</p>
                    <h2>Discover Craftsmanship Unbowed by Time</h2>
                    <p class="lead">Japan’s rural areas are a rich source of skilled craftsmen and exquisite traditional handicrafts that harness elements of nature. For centuries, skilled artisans have carefully handed down the secrets behind the creation of <i>katana</i> (sword) steel, porcelain, soy sauce, textiles and a pigment called “Japan Red.” Explore these untold stories behind time-honored manufacturing techniques and their cultural legacy.</p>
                </div>
            </div>
            <div class="img"><img src="../../assets/spirit_of_japan/main01.jpg" alt="" class="_pc"><img src="../../assets/spirit_of_japan/main01_sp.jpg" alt="" class="_sp"></div>
        </section>
        <section class="basic-section">
            <div class="para -reverse">
                <div class="img"><img src="../../assets/spirit_of_japan/img01.jpg" alt=""></div>
                <div class="txt">
                    <h3>The Reddish Pigment that Symbolizes Japan</h3>
                    <p>The soft reddish-brown pigment synonymous with Japan is everywhere in Fukiya, an old mining town tucked away in the Okayama Prefecture mountains. This iron-oxide pigment, called <i>bengara</i> and known globally as “Japan Red,” adds a distinctive touch to the town’s temples, shrines and other buildings, and features prominently in ceramics, lacquerware and other traditional local crafts. Fukiya was once Japan’s top producer of <i>bengara</i>, which proudly makes its presence felt on many roof tiles, lattices and elsewhere in the well-preserved townscape. Some shops in Fukiya allow visitors to dye their own souvenirs so they can take away a colorful memento of <i>bengara</i> craftsmanship.</p>
                    <div class="btns">
                        <ul>
                            <li>
                                <router-link :to="{name: 'Popular', params: {id: 'a6332e44-a39b-44db-ad16-8f8065d54394', page: 'about', url: 'birthplace-japan-red-bichu-fukiya-town-bengara-copper-okayama'}}" class="-black">
                                    <span>Read More</span>
                                </router-link>
                            </li>
                            <li>
                                <router-link :to="{name: 'LuxuryTravel05'}" class="-gold">
                                    <span>Luxury</span>
                                </router-link>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </section>
        <section class="basic-section">
            <div class="para">
                <div class="img"><img src="../../assets/spirit_of_japan/img02.jpg" alt=""></div>
                <div class="txt">
                    <h3>The Secrets of Japanese Porcelain</h3>
                    <p>
                        The Hizen region, which straddles Saga and Nagasaki Prefectures, was the birthplace of Japanese porcelain more than 400 years ago. Hizen had plentiful supplies of porcelain clay, wood to fuel the kilns, water, and other natural resources required to successfully sustain the ceramics industry. Craftsmen from far and wide were drawn to Hizen, where their sharing of ideas and techniques led to creations including gorgeously colored porcelain adorned with gold, and porcelain as thin as eggshell. Some of these were exported and enraptured overseas royalty, heavily influencing porcelain in Europe.<br>
                        Kilns still operate in locations including Okawachiyama, which was known as the “village of secret kilns” because the ruling clan kept this mountain village isolated to protect its production secrets. Many of Hizen’s porcelain-production areas have retained their quaint townscapes and atmospheres of years past, including old kilns, brick chimneystacks and walls made from reused kiln bricks. Workshops give visitors the chance to make or decorate their own piece of ware, keeping Hizen’s porcelain history and culture alive.
                    </p>
                    <div class="btns">
                        <ul>
                            <li>
                                <router-link :to="{name: 'Popular', params: {id: 'e561822d-ce94-4557-9f1a-7d6b09ee543b', page: 'about', url: 'hizen-region-cradle-japanese-porcelain-saga-nagasaki'}}" class="-black">
                                    <span>Read More</span>
                                </router-link>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </section>
        <section class="basic-section">
            <div class="para -reverse">
                <div class="img"><img src="../../assets/spirit_of_japan/img03.jpg" alt=""></div>
                <div class="txt">
                    <h3>1,400 Years of Steel Manufacturing for Katana Swords</h3>
                    <p>
                        Elegant yet deadly, <i>katana</i> were more than just a weapon. They were considered an extension of a samurai warrior’s soul. Forged with extreme strength and precision, a <i>katana</i> is a timeless symbol of the samurai spirit.<br>
                        The traditional <i>tatara</i> method of heating iron sand in a clay furnace has been integral to the production of the steel blades that gave <i>katana</i> their fearsome reputation. This method began to flourish in what is now Izumo, Shimane Prefecture, about 1,400 years ago and was painstakingly perfected and preserved. Over the centuries, vast amounts of iron sand were excavated from the mountainous landscape to sustain this industry. The scarred mountains were later carefully converted into terraced rice fields, maintaining a delicate balance between man and nature.<br>
                        The ironmaking industry was vital to Japan’s cultural heritage, and Izumo is the only place in the world where the fires of a <i>tatara</i> furnace still burn as they did centuries ago.
                    </p>
                    <div class="btns">
                        <ul>
                            <li>
                                <router-link :to="{name: 'Popular', params: {id: '9908e8af-375b-44b0-a81a-3121b3059a3d', page: 'about', url: 'izumo\'s-ancient-tatara-ironmaking-heritage-shimane-iron-sword'}}" class="-black">
                                    <span>Read More</span>
                                </router-link>
                            </li>
                            <li>
                                <router-link :to="{name: 'LuxuryTravel06'}" class="-gold">
                                    <span>Luxury</span>
                                </router-link>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </section>
        <section class="basic-section">
            <div class="para">
                <div class="img"><img src="../../assets/spirit_of_japan/img04.jpg" alt=""></div>
                <div class="txt">
                    <h3>A Textile Industry that Bloomed from a Single Cotton Ball</h3>
                    <p>
                        The planting of cotton on reclaimed land near Kurashiki about 400 years ago laid the foundation for a textile industry that evolved into a part of Japan’s cultural heritage.<br>
                        Since the 19th century, the harnessing and blending of Western technologies with Japanese traditions helped Kurashiki rise to prominence as a “textile town.” Kurashiki was the birthplace of <i>kinkan’en</i>, superb-quality rush mats woven in elaborate and colorful floral patterns. In more recent times, Japan’s first domestically produced denim jeans originated in Kurashiki, and the city is now home to premium jeans manufacturers and shops.<br>
                        Numerous white-walled homes of merchants who made their fortune through spinning still stand along with well-preserved Western-style buildings, giving visitors a glimpse into how the city looked during a time of prosperity.
                    </p>
                    <div class="btns">
                        <ul>
                            <li>
                                <router-link :to="{name: 'Popular', params: {id: 'd3416939-820e-4c79-9de8-16399517b6c7', page: 'about', url: 'kurashiki-story-fiber-cotton-okayama'}}" class="-black">
                                    <span>Read More</span>
                                </router-link>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </section>
        <section class="basic-section">
            <div class="para -reverse">
                <div class="img"><img src="../../assets/spirit_of_japan/img05.jpg" alt=""></div>
                <div class="txt">
                    <h3>The Miracle Drip that became Soy Sauce</h3>
                    <p>
                        Soy sauce has become a classic condiment around the world, but it is said to have been discovered by accident when a miso maker in Kishu Yuasa in Wakayama Prefecture tasted a single drip of the dark liquid that had accumulated in the tubs during the miso production process. This miraculous moment changed Japanese cuisine.<br>
                        Soy sauce production prospered and had become a major industry in Yuasa by the 16th century. Many traditional storehouses and <i>machiya</i> townhouses still stand in the town, providing a picturesque reminder of the town’s heyday.<br>
                        Truly authentic soy sauce gets its taste from the bacteria and yeasts that cover the ceiling and the beams of long-established fermentation rooms, giving each brewery its own distinctive flavor. The yeasts and the steady hands of skilled brewers have kept the traditional taste of soy sauce alive to this day.<br>
                        A visit to Yuasa would not be complete without eating local delicacies such as <i>shirasu-don</i> (whitebait on rice) that feature miso and soy sauce.
                    </p>
                    <div class="btns">
                        <ul>
                            <li>
                                <router-link :to="{name: 'Popular', params: {id: '81ba1d75-0043-4fe2-8ccd-824b9e1a1cbc', page: 'about', url: 'kishu-yuasa-soy-sauce-wakayama-kinzanji-miso'}}" class="-black">
                                    <span>Read More</span>
                                </router-link>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
    </section>
    <section class="section03">
            <div class="inner">
                <h2>Other Articles</h2>
                <ul>
                    <li>
                        <router-link :to="{name: 'SpiritOfJapan2'}">
                            <div class="img"><img src="../../assets/spirit_of_japan2/other.jpg" alt=""></div>
                            <div class="text">
                                <div class="headline">Spirit of Japan</div>
                                <div class="title">Sacred Sites where Nature and Faith Blend into One</div>
                            </div>
                        </router-link>
                    </li>
                    <li>
                        <router-link :to="{name: 'SpiritOfJapan3'}">
                            <div class="img"><img src="../../assets/spirit_of_japan3/other.png" alt=""></div>
                            <div class="text">
                                <div class="headline">Spirit of Japan</div>
                                <div class="title">Unlocking the Secrets of the Shogun, Samurai and Ninja</div>
                            </div>
                        </router-link>
                    </li>
                    <li>
                        <router-link :to="{name: 'SpiritOfJapan4'}">
                            <div class="img"><img src="../../assets/spirit_of_japan4/other.webp" alt=""></div>
                            <div class="text">
                                <div class="headline">Spirit of Japan</div>
                                <div class="title">The Journey of Japan's Original Landscapes Shaped by Water</div>
                            </div>
                        </router-link>
                    </li>
                    <li>
                        <router-link :to="{name: 'SpiritOfJapan5'}">
                            <div class="img"><img src="../../assets/spirit_of_japan5/other.webp" alt=""></div>
                            <div class="text">
                                <div class="headline">Spirit of Japan</div>
                                <div class="title">Encounter Festivals Embodying the Japanese Spirit</div>
                            </div>
                        </router-link>
                    </li>
                    <li>
                        <router-link :to="{name: 'SpiritOfJapan6'}">
                            <div class="img"><img src="../../assets/spirit_of_japan6/other.webp" alt=""></div>
                            <div class="text">
                                <div class="headline">Spirit of Japan</div>
                                <div class="title">Ancient Paths
                                    Where Japanese Culture Dwells</div>
                            </div>
                        </router-link>
                    </li>
                    <li>
                        <router-link :to="{name: 'SpiritOfJapan7'}">
                            <div class="img"><img src="../../assets/spirit_of_japan7/main01.webp" alt=""></div>
                            <div class="text">
                                <div class="headline">Spirit of Japan</div>
                                <div class="title">“O-sake” — the drink that nurtured Japanese history</div>
                            </div>
                        </router-link>
                    </li>
                </ul>
            </div>
        </section>
    </div>
</template>

<script>
import ApiRouter from "../../components/Systems/ApiRouter";
import JsonLd from "@/components/JsonLd/JsonLd.vue";

export default {
    name: "PlanYourVisit",
    components: {JsonLd},
    mixins: [ApiRouter],
    data: function () {
        return {
            articles: [],
            activeTabMenu: 'half',
            jsonld: {
                "@context": "https://schema.org",
                "@type": "Article",
                "mainEntityOfPage": window.location.href,
                "headline": "Discover Craftsmanship Unbowed by Time",
                "description": "Japan Heritage ties together historical sites, shrines, temples, traditional arts, castles, armor," +
                    "and other cultural assets. Find photos, videos, tourist information, and more.",
                "image": [
                    require('../../assets/spirit_of_japan/main01.jpg')
                ],
                "datePublished": "2024/08/30",
                "dateModified": "2024/11/13",
                "author": [{
                    "@type": "Organization",
                    "name": "Japan National Tourism Organization",
                    "url": "https://www.japan.travel/japan-heritage/"
                }]
            }
        }
    },
    created: function () {
        this.loadData();
        this.getQuery();
        document.title = 'Spirit Of Japan | Japan Heritage Official Site'
    },
    methods: {
        loadData: function () {
            window.axios.get(this.routes.articles)
                .then((response) => {
                    this.articles = response.data.data;
                });
        },
        getQuery: function () {
            const tab = this.$route.query.tab;

            switch (tab) {
                case 'day1':
                    this.activeTabMenu = 'day1';
                    break;
                case 'day2':
                    this.activeTabMenu = 'day2';
                    break;
                case 'day3':
                    this.activeTabMenu = 'day3';
                    break;
                default:
                    this.activeTabMenu = 'half';
                    break;
            }
        },
        articleCardHasClicked: function (id, hash, breadcrumb) {
            this.$router.push({name: 'Popular', params: {id: id, page: 'travelInfo', breadcrumb: breadcrumb}, hash: hash});
        }
    },
    watch: {
        $route() {
            this.getQuery();
        }
    }
}
</script>

<style src="./SpiritOfJapan.scss" lang="scss" scoped />
